import { v4 as uuidv4 } from 'uuid';
import { useCallback, useState } from 'react';
import type { DesignDocumentWithPanels, Image } from '@mcp-artwork/cimdoc';
import { Toggle, FlexBox, Button } from '@cimpress/react-components';
import { Page, PreviewSection, PreviewRows } from '../Components';
import { presets } from '../data/defaultSamples/imageToCimdocPresets';
import { getCimpressDocumentV2, getCimdocEnrichmentUrl, type CustomInputType } from '../api/urlHelpers';
import type { PreviewDataLoader } from '../Components/common/previewCard/types';
import { useUploadAndPresetCards } from '../Components/CimdocEnrichment/UseUploadAndPresetCards';

export const CimDocEnrichmentPage = () => {
    const [fileUrl, setFileUrl] = useState<string | undefined>();
    const [transformSpace, setTransformSpace] = useState<'raster' | 'vector'>('vector');
    const [documentLoaders, setDocumentLoaders] = useState<Record<string, PreviewDataLoader<'document'>>>({});

    const processImage = useCallback(
        async (fileUrl: string, previewImageUrl: string, type: CustomInputType) => {
            try {
                const enrichmentLoader = async ({
                    signal,
                }: {
                    onStatusUpdate: (message: string) => void;
                    signal: AbortSignal;
                }) => {
                    try {
                        const documentUrl = await getCimdocEnrichmentUrl(
                            fileUrl,
                            previewImageUrl,
                            type,
                            transformSpace,
                        );
                        if (!documentUrl) throw new Error('No document URL returned');

                        const cimDoc = await getCimpressDocumentV2(documentUrl, signal);
                        const firstPanel = (cimDoc.document as DesignDocumentWithPanels).panels?.[0];

                        if (firstPanel) {
                            const panelCopy = { ...firstPanel };
                            panelCopy.id = uuidv4();
                            panelCopy.name = 'original-raster-panel';
                            panelCopy.images = [
                                {
                                    id: uuidv4(),
                                    previewUrl: previewImageUrl,
                                    printUrl: previewImageUrl,
                                    position: {
                                        x: '0mm',
                                        y: '0mm',
                                        width: firstPanel.width,
                                        height: firstPanel.height,
                                    },
                                    horizontalAlignment: 'left',
                                    verticalAlignment: 'top',
                                    rotationAngle: '0',
                                    zIndex: 100,
                                } as Image,
                            ];
                            panelCopy.shapes = [];
                            panelCopy.textAreas = [];
                            panelCopy.itemReferences = [];
                            (cimDoc.document as DesignDocumentWithPanels).panels?.push(panelCopy);
                        }

                        return { document: cimDoc };
                    } catch (error) {
                        console.error('Error enriching document:', error);
                        throw error;
                    }
                };
                setDocumentLoaders((prev) => ({ ...prev, [fileUrl]: enrichmentLoader }));
            } catch (error) {
                console.error(error);
            }
        },
        [transformSpace],
    );

    const onChange = useCallback(
        (url: string | undefined) => {
            if (!url) return;
            setFileUrl(url);
        },
        [setFileUrl],
    );

    const rows = useUploadAndPresetCards({
        imageCardData: presets,
        documentLoaders,
        processImage,
        onChange,
        fileUrl,
    });

    return (
        <Page>
            <Page.Content fullWidth>
                <Page.Title>Image to Cimdoc Examples</Page.Title>
            </Page.Content>
            <Page.Controls fullWidth>
                <div
                    style={{
                        background: '#f7f7f7',
                        borderRadius: '8px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#666', marginBottom: 0 }}>
                        In Progress Features
                    </div>
                    <FlexBox justifyContent="flex-start" alignItems="center" style={{ gap: '48px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontWeight: 500 }}>Transform Space:</span>
                            <Toggle
                                on={transformSpace === 'vector'}
                                onClick={() => setTransformSpace(transformSpace === 'raster' ? 'vector' : 'raster')}
                                size="sm"
                            />
                            <span style={{ fontSize: '13px', color: '#555', marginLeft: '8px' }}>
                                {transformSpace === 'raster' ? 'Raster' : 'Vector'}
                            </span>
                            {fileUrl && (
                                <>
                                    <span style={{ padding: '0 10px' }}>|</span>
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            if (fileUrl && documentLoaders[fileUrl]) {
                                                const loaders = { ...documentLoaders };
                                                delete loaders[fileUrl];
                                                setDocumentLoaders(loaders);
                                            }
                                            setFileUrl('');
                                        }}
                                    >
                                        Reset custom card
                                    </Button>
                                </>
                            )}
                        </div>
                    </FlexBox>
                </div>
            </Page.Controls>
            <Page.Content fullWidth>
                <PreviewSection
                    introduction="Upload your own image or try one of our presets"
                    showIntro={false}
                    rows={PreviewRows(rows)}
                />
            </Page.Content>
        </Page>
    );
};
